// Subscriptions mode switcher — a copy of src/blank/BlankLayout.tsx (BP-14/BP-85). Modes are ROUTES;
// record pages (:id, create) are SIBLINGS of this layout.
import { useEffect } from "react";

import { Box, Button, Typography } from "@mui/material";
import { usePopupState } from "material-ui-popup-state/hooks";
import { Outlet, useLocation } from "react-router-dom";

import {
  BreadcrumbChevronIcon,
  BreadcrumbDropdownMenu,
  BreadcrumbHoverRoot,
  BreadcrumbMenuLinkItem,
  describeMode,
} from "../components/BreadcrumbDropdownMenu";
import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";
import { BreadcrumbHeaderListing, Title } from "../components/Header";
import { LinkRouter } from "../components/Links";
import { ListingRouteBaseProvider } from "../components/datagrid/ListingRouteBase";
import {
  breadcrumbCurrentLabelSx,
  breadcrumbCurrentTriggerSx,
  breadcrumbTitleSx,
} from "../components/dropdownMenuStyles";
import { migrateModeSegment } from "../components/renamedModeSegments";


/** Mode segment -> dropdown label. Keys are the child route paths. */
export const breadcrumbMenu: { [segment: string]: string } = {
  // BP-14: Subscriptions is a MAJOR module (Aaron, H229: the renewal alerts are read at a glance,
  // "Move to patterns"), so it gets a Dashboard mode — segment `dashboard`, never "overview".
  dashboard: "Dashboard",
  list: "Subscriptions",
  pipeline: "Pipeline",
  calendar: "Calendar",
};

/**
 * Mode segment -> the grey line under the label in the dropdown (BP-85:
 * Aaron 2026-09-28, "ALL model switchers should have name and description
 * the same as demand"). Inventory / Demand is the reference: 2-4 words,
 * sentence case, naming what the view HOLDS — read off the mode's component,
 * never paraphrased from the label. Same keys as `breadcrumbMenu`; a key
 * missing here renders the label alone (the fallback), and
 * src/__tests__/modeDescriptions.test.ts fails a module that leaves one out.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  // SubscriptionsDashboard: five alert tiles, the stage donut, the soonest renewals.
  dashboard: "Renewal alerts and stages",
  // SubscriptionsList: the AeronetDataGrid register, an Alert column + filter.
  list: "Every subscription",
  // SubscriptionsBoard mode="pipeline": stage columns x due-month lanes.
  pipeline: "By stage and month",
  // SubscriptionsBoard mode="calendar": month columns x brand lanes.
  calendar: "By month and brand",
};

// The Dashboard, as Blank's (BlankLayout.tsx): a first visit lands on the alerts. A remembered mode
// still wins (readRememberedModeSegment), so anyone who last used the list returns to it.
export const DEFAULT_MODE_SEGMENT = "dashboard";

// Remember the last mode so landing on the bare module root returns the user
// where they left off ("the user expects the choice to stick across visits" —
// PATTERNS.md mode-switch section, key shape `<module>.viewMode`). Routes stay
// the source of truth for WHICH mode is showing; localStorage only records the
// last one.
export const MODE_STORAGE_KEY = "subscriptions.viewMode";

/**
 * Is this a real mode segment? Uses hasOwnProperty, NOT `in` — `in` walks the
 * prototype chain, so "constructor" / "toString" / "valueOf" / "__proto__" all
 * test true against an object literal and would pass as valid modes.
 */
const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(breadcrumbMenu, segment);

/** Redirect target for a bare visit to the module root. */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  // A renamed segment ("overview") maps to its replacement before validation,
  // or every user who last used it is silently reset to the default.
  const stored = migrateModeSegment(window.localStorage.getItem(MODE_STORAGE_KEY));
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

interface SubscriptionsLayoutProps {
  /** Module title from the nav; roots the breadcrumb. */
  title?: string;
}

const SubscriptionsLayout: React.FC<SubscriptionsLayoutProps> = ({ title }) => {
  const location = useLocation();
  const popupState = usePopupState({
    variant: "popover",
    popupId: "subscriptionsBreadcrumbMenu",
  });

  // Split the live pathname so the trailing mode segment can be told apart
  // from the (nav-driven) module base. On "/subscriptions/pipeline" that gives
  // basePath "/subscriptions" and currentMode "pipeline".
  const segments = location.pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const currentMode = onMode ? lastSegment : DEFAULT_MODE_SEGMENT;
  // Empty-check before prefixing the slash: a bare "/" + "" would make the
  // mode links "//dashboard", which a browser resolves as a PROTOCOL-RELATIVE
  // URL (host "dashboard") and navigates off-site.
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  const basePath = baseSegments.length ? "/" + baseSegments.join("/") : "";

  useEffect(() => {
    if (onMode) window.localStorage.setItem(MODE_STORAGE_KEY, lastSegment);
  }, [onMode, lastSegment]);

  return (
    <Box height="100%">
      <BreadcrumbHeaderListing aria-label="breadcrumb">
        <Title sx={breadcrumbTitleSx}>
          <LinkRouter to={basePath || "/"}>
            {title || "Subscriptions"} /
          </LinkRouter>
          <BreadcrumbHoverRoot popupState={popupState}>
            <Button
              endIcon={<BreadcrumbChevronIcon />}
              sx={breadcrumbCurrentTriggerSx}
            >
              <Typography
                variant="h2"
                component="span"
                sx={breadcrumbCurrentLabelSx}
              >
                {breadcrumbMenu[currentMode]}
              </Typography>
            </Button>
            <BreadcrumbDropdownMenu popupState={popupState}>
              {Object.entries(breadcrumbMenu).map(([segment, label]) => (
                <BreadcrumbMenuLinkItem
                  key={segment}
                  to={`${basePath}/${segment}`}
                  label={label}
                  description={describeMode(MODE_DESCRIPTIONS, segment)}
                  selected={currentMode === segment}
                  onClick={popupState.close}
                />
              ))}
            </BreadcrumbDropdownMenu>
          </BreadcrumbHoverRoot>
        </Title>
      </BreadcrumbHeaderListing>
      {/* SubscriptionsList renders the shared DataGrid's default toolbar (create FAB
          enabled, no toolbar override), and its listing now lives one segment
          below the module base ("list"), so the FAB's bare relative "create"
          would resolve to <base>/list/create — a route that does not exist
          (the CANONICAL instance of the bug this reference module exists to
          demonstrate the fix for; see
          src/components/datagrid/ListingRouteBase.tsx and
          src/companies/CompaniesLayout.tsx, the first module converted).
          Declaring the base here, once, covers both modes under this layout —
          including Dashboard if it ever grows a grid of its own. */}
      <ListingRouteBaseProvider value={{ base: basePath }}>
        <Outlet />
      </ListingRouteBaseProvider>
    </Box>
  );
};

export default SubscriptionsLayout;
